import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { useMutation } from '@/utils/api';

interface HealthReportResponse {
  status: string;
  path?: string;
}

interface FileResponse {
  url: string;
}

interface UseGenerateHealthReportReturn {
  generate: () => void;
  isGenerating: boolean;
}

/**
 * Hook to trigger on-demand health report generation.
 *
 * POST /report/health — synchronous Lambda invocation (≤30s)
 * On success: fetches presigned download URL from GET /file?name={path}
 * Shows toast with "Download PDF" action button.
 * Handles "already in progress" error from HasRunningReportJob guard.
 */
export function useGenerateHealthReport(): UseGenerateHealthReportReturn {
  const axios = useAxios();

  const mutation = useMutation<void, Error, void>({
    defaultErrorMessage: 'Failed to generate health report',
    mutationFn: async () => {
      const toastId = toast.loading('Generating health report...', {
        duration: Infinity,
      });

      try {
        const response =
          await axios.post<HealthReportResponse>('/report/health');

        toast.dismiss(toastId);

        const pdfPath = response.data?.path;

        if (pdfPath) {
          const fileRes = await axios.get<FileResponse>('/file', {
            params: { name: pdfPath },
          });

          toast.success('Health report generated', {
            duration: 15000,
            action: {
              label: 'Download PDF',
              onClick: () => {
                const url = fileRes.data.url;
                if (!/^https?:\/\//i.test(url)) return;
                window.open(url, '_blank', 'noopener,noreferrer');
              },
            },
          });
        } else {
          toast.success('Risk Score Report has been emailed.');
        }
      } catch (error) {
        toast.dismiss(toastId);

        if (
          error instanceof Error &&
          error.message.toLowerCase().includes('already in progress')
        ) {
          toast.error('A report is already being generated. Please wait.');
        } else {
          toast.error(
            error instanceof Error
              ? error.message
              : 'Failed to generate health report',
            { duration: 8000 }
          );
        }

        throw error;
      }
    },
  });

  return {
    generate: mutation.mutate,
    isGenerating: mutation.isPending,
  };
}
